import { getUserId } from "../../lib/effectiveAuthUser";
import { useAdminAccess } from "src/hooks/useAdminAccess";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Auth from "../../Auth";
import { useStableCallback } from "src/hooks/useStableCallback";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { TONES } from "../../../../src/shared/constants";
import { STORY_BIBLE_STYLE_ID, STORY_BIBLE_STYLE_NAME, STYLE_GUIDE_BUILT_IN_STYLES } from "../../../../src/shared/styleGuideBuiltInStyles.js";
import useImportSpeedPerceptionExperiment, { isImportSpeedPerceptionActive } from "../../hooks/useImportSpeedPerceptionExperiment";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { useToggleCommentMode } from "../../hooks/useToggleCommentMode";
import mixpanel from "../../MixpanelConfiguration";
import ProjectModel from "../../models/Project";
import PageNotFound from "../../PageNotFound";
import update from "react-addons-update";
import SettingsPanel from "../../SettingsPanel";
import { useAppStore } from "../../stores/appStore";
import { useProjectStore } from "../../stores/projectStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import Banners from "../Banners";
import EmailReviewBanner from "../EmailReviewBanner";
import DocumentSharingMenu, { useCurrentlySharedDocuments } from "../DocumentSharingMenu";
import EditorMessage from "../EditorMessage";
import HelpMenu from "../HelpMenu";
import { LeftBarContainer } from "../LeftBarContainer";
import ProjectScenesUpdate from "../Modals/ProjectScenesUpdate";
import MultipleAccountCTA from "../MultipleAccountCTA";
import GraceCTA from "../Pricing/GraceCTA";
import OverageNotice from "../Pricing/OverageNotice";
import QuotaZeroCTA from "../Pricing/QuotaZeroCTA";
import QuotaMessageV1 from "../QuotaMessageV1";
import { useStructuredOutlineStore } from "../StoryBible/StructuredOutline/StructuredOutlineStore";
import { ProjectTopBar } from "../ProjectTopBar";
import ExitTourButton from "../StickyTour/ExitTourButton";
import { TourIds } from "../StickyTour/TourTypes";
import TiptapDocumentSB from "../TiptapDocumentSB";
import Menu from "../TiptapDocumentSB/Menu";
import DocumentAdminMenu from "./DocumentAdminMenu";
import ImportProgressBanner from "./ImportProgressBanner";
import ProjectSpacingMigrationProgressBanner from "./ProjectSpacingMigrationProgressBanner";
import { StyledContainer } from "./styles";
import { useDocumentUrlSync } from "./useDocumentUrlSync";
import { getProjectTopBarSelectedStyleName } from "./styleGuideLabel";

const STORY_BIBLE_STYLE_ROW = {
  source: "story_bible",
  id: STORY_BIBLE_STYLE_ID,
  name: STORY_BIBLE_STYLE_NAME,
  kind: "story_bible",
  instructions: "",
  hidden: false,
  virtual: true,
};

const STYLE_GUIDE_BUILT_IN_ROWS = STYLE_GUIDE_BUILT_IN_STYLES.map((style) => ({
  source: "sudowrite",
  id: style.id,
  name: style.name,
  tag: style.tag,
  kind: "sudowrite",
  description: style.description,
  instructions: style.promptStyleText,
  hidden: false,
}));
const STYLE_GUIDE_STYLES_CHANGED_EVENT = "style-guide-styles-changed";

function ensureStyleGuideRows(styles, legacyStyle = "") {
  const rows = Array.isArray(styles) ? styles : [];
  const existingBuiltInIds = new Set(rows.filter((style) => style.source === "sudowrite").map((style) => style.id));
  const incomingStoryBibleRow = rows.find((style) => style.source === "story_bible") || {
    ...STORY_BIBLE_STYLE_ROW,
    instructions: legacyStyle || STORY_BIBLE_STYLE_ROW.instructions,
  };
  const { tag: _storyBibleTag, ...storyBibleRow } = incomingStoryBibleRow;
  return [
    storyBibleRow,
    ...rows.filter((style) => style.source !== "story_bible"),
    ...STYLE_GUIDE_BUILT_IN_ROWS.filter((style) => !existingBuiltInIds.has(style.id)),
  ];
}

function resolveSelectedStyleInstructions(styles, selectedStyle, legacyStyle = "") {
  const rows = Array.isArray(styles) ? styles : [];
  const legacyStoryBibleStyle = legacyStyle?.trim?.() || "";

  if (!selectedStyle) {
    const storyBibleRow = rows.find((style) => style.source === "story_bible");
    return storyBibleRow?.instructions?.trim?.() || legacyStoryBibleStyle;
  }

  if (selectedStyle.source === "story_bible" || selectedStyle.source === "user_style") {
    const selectedRow = rows.find((style) => style.source === selectedStyle.source && style.id === selectedStyle.id);
    if (selectedStyle.source === "story_bible") {
      return selectedRow?.instructions?.trim?.() || legacyStoryBibleStyle;
    }
    return selectedRow?.instructions?.trim?.() || "";
  }

  if (selectedStyle.source === "sudowrite") {
    const selectedRow = rows.find((style) => style.source === "sudowrite" && style.id === selectedStyle.id);
    return selectedRow?.instructions?.trim?.() || selectedRow?.description?.trim?.() || "";
  }

  return legacyStoryBibleStyle;
}

export default function Project({
  user,
  subscription, //
  setSettingsOpen,
  setHelpOpen,
  theme,
  totalDocuments,
  needsFeedback,
  updateNeedsFeedback,
  hideEDUCards,
  updateHideEDUCards,
  editorOptions,
  setEditorOptions,
  summaryEnabled,
  expandEnabled,
  filterEnabled,
  userData,
  wormholeOutputLength,
  wormholeOutputCards,
  creativitySliderPosition,
  writeModel,
  showFront,
}) {
  // Store state variables and setters
  const reset = useProjectStore((state) => state.reset);
  const project = useProjectStore((state) => state.project);
  // ProjectTopBar (SW-7957) — share/help/settings panels need these from sudowriteStore
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const settingsOpen = useSudowriteStore((state) => state.settingsOpen);
  const helpOpen = useSudowriteStore((state) => state.helpOpen);
  const demo = useSudowriteStore((state) => state.demo);
  const setDemo = useSudowriteStore((state) => state.setDemo);
  const openStyleSettings = useSudowriteStore((state) => state.openStyleSettings);
  const importSpeedPerceptionExperiment = useImportSpeedPerceptionExperiment();
  const importSpeedPerceptionActive = isImportSpeedPerceptionActive(importSpeedPerceptionExperiment, project?.id);
  const setProject = useProjectStore((state) => state.setProject);
  const contents = useProjectStore((state) => state.contents);
  const setContents = useProjectStore((state) => state.setContents);
  const document = useProjectStore((state) => state.document);
  const setDocument = useProjectStore((state) => state.setDocument);
  const setDeletedDocuments = useProjectStore((state) => state.setDeletedDocuments);
  const deletedDocuments = useProjectStore((state) => state.deletedDocuments);
  const editingTitle = useProjectStore((state) => state.editingTitle);
  const isEditing = useProjectStore((state) => state.isEditing);
  const isEditingDocumentTitle = useProjectStore((state) => state.isEditingDocumentTitle);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const innerRef = useProjectStore((state) => state.innerRef);
  const menuRef = useProjectStore((state) => state.menuRef);
  const editorSectionRef = useProjectStore((state) => state.editorSectionRef);
  const setDocumentId = useProjectStore((state) => state.setDocumentId);
  const selectedDocumentId = useProjectStore((state) => state.selectedDocumentId);
  const trashedItem = useProjectStore((state) => state.trashedItem);
  const setIsEditingTextarea = useProjectStore((state) => state.setIsEditingTextarea);
  const creatingDocument = useProjectStore((state) => state.creatingDocument);
  const setCreatingDocument = useProjectStore((state) => state.setCreatingDocument);
  const setIsCreatingNewBlankDocument = useProjectStore((state) => state.setIsCreatingNewBlankDocument);
  const isTourActive = useStickyTourStore((state) => state.isTourActive);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const tourSteps = useStickyTourStore((state) => state.steps);
  const currentTourStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const activeTourStepId = tourSteps[currentTourStepIndex]?.id;
  const resetOutline = useStructuredOutlineStore((state) => state.reset);
  const setRightBarCollapsed = useProjectStore((state) => state.setRightBarCollapsed);
  const setLeftBarCollapsed = useProjectStore((state) => state.setLeftBarCollapsed);

  // URL params
  const { id } = useParams();
  useCurrentlySharedDocuments(id); // fetch shared documents
  const { toggleComments } = useToggleCommentMode();

  // Local state variables
  const [notFound, setNotFound] = useState(false);
  const [leftBarHeight, setLeftBarHeight] = useState(0);
  const [locked, setLocked] = useState(null);
  const importRestrictedUiRef = useRef({ active: false, savedSb: null, savedLeftCollapsed: null, savedRightCollapsed: null });

  // Write states
  const [tone, setTone] = useState(null);
  const [writeMode, setWriteMode] = useState(null);
  const [describeSenses, setDescribeSenses] = useState(null);
  const [automaticGuidances, setAutomaticGuidances] = useState(null);
  const [memory, setMemory] = useState(null);
  const [memoryEnabled, setMemoryEnabled] = useState(null);

  // Doucument related
  const [isDeleted, setIsDeleted] = useState(false);
  const [documentAdminOpen, setDocumentAdminOpen] = useState(false);
  const [saved, setSaved] = useState("saving");
  const [topBarWordCount, setTopBarWordCount] = useState(null);
  const [topBarHighlightedWordCount, setTopBarHighlightedWordCount] = useState(0);

  const { hash, pathname } = useLocation();
  const history = useHistory();

  const isOnline = useOnlineStatus();

  // 2.0 release related
  const isSW2 = useAppStore((state) => state.isSW2);
  const isDocumentRouteLoading = isSW2 && !document && (pathname.includes("/document/") || selectedDocumentId || contents?.length > 0);
  const [showSBmodal, setShowSBmodal] = useState(false);
  const storyBibleInView = useStoryBibleStore((state) => state.isInView);
  const storyBibleMetadata = useStoryBibleStore((state) => state.metadata);
  const resetDocumentDependentState = useStoryBibleStore((state) => state.resetDocumentDependentState);
  const [styleGuideOpen, setStyleGuideOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [styleGuideStyles, setStyleGuideStyles] = useState([]);
  const [initialStyleGuideFlow, setInitialStyleGuideFlow] = useState("list");
  const [initialStyleGuideFlowRequestId, setInitialStyleGuideFlowRequestId] = useState(0);
  const storyBibleStyleText = useMemo(
    () => resolveSelectedStyleInstructions(styleGuideStyles, selectedStyle, storyBibleMetadata?.style ?? ""),
    [selectedStyle, storyBibleMetadata?.style, styleGuideStyles],
  );
  const selectedStyleName = getProjectTopBarSelectedStyleName({
    selectedStyle,
    canonicalStyle: "",
    legacyStyle: storyBibleMetadata?.style,
  });
  const lastStylesListResponseRef = useRef(null);

  const applyStyleGuideListResponse = useCallback((data, legacyStyle = "") => {
    if (!data) return;
    const styles = ensureStyleGuideRows(data?.styles, legacyStyle);
    const nextSelectedStyle = data?.selectedStyle ?? null;
    setStyleGuideStyles(styles);
    setSelectedStyle(nextSelectedStyle);
  }, []);

  const loadStyleGuideStyles = useCallback(async (isCancelled = () => false) => {
    if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) {
      lastStylesListResponseRef.current = null;
      setSelectedStyle(null);
      setStyleGuideStyles([]);
      return;
    }

    try {
      const data = await Auth.fetchWithIdToken("/api/style-settings/styles/list", { projectId: project.id });
      if (isCancelled()) return;
      lastStylesListResponseRef.current = data;
      applyStyleGuideListResponse(data, useStoryBibleStore.getState().metadata?.style ?? "");
    } catch (error) {
      if (isCancelled()) return;
      lastStylesListResponseRef.current = null;
      setSelectedStyle(null);
      setStyleGuideStyles([]);
      Sentry.captureException(error);
    }
  }, [applyStyleGuideListResponse, project?.id, subscriptionJSON?.hasAccessToStyleGuide]);

  useEffect(() => {
    let cancelled = false;
    void loadStyleGuideStyles(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [loadStyleGuideStyles]);

  useEffect(() => {
    lastStylesListResponseRef.current = null;
  }, [project?.id]);

  useEffect(() => {
    if (!lastStylesListResponseRef.current) return;
    applyStyleGuideListResponse(lastStylesListResponseRef.current, storyBibleMetadata?.style ?? "");
  }, [applyStyleGuideListResponse, storyBibleMetadata?.style]);

  useEffect(() => {
    if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) return undefined;

    let cancelled = false;
    let requestId = 0;
    const handleStyleGuideStylesChanged = (event) => {
      const changedProjectId = event?.detail?.projectId;
      if (changedProjectId && changedProjectId !== project.id) return;
      const currentRequestId = ++requestId;
      void loadStyleGuideStyles(() => cancelled || currentRequestId !== requestId);
    };

    window.addEventListener(STYLE_GUIDE_STYLES_CHANGED_EVENT, handleStyleGuideStylesChanged);
    return () => {
      cancelled = true;
      requestId += 1;
      window.removeEventListener(STYLE_GUIDE_STYLES_CHANGED_EVENT, handleStyleGuideStylesChanged);
    };
  }, [loadStyleGuideStyles, project?.id, subscriptionJSON?.hasAccessToStyleGuide]);

  const handleBlankStyleSave = useCallback(
    async ({ id, name = "Untitled Style", instructions = "", writingSample, simpleControls, source = "blank" }) => {
      if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) return;

      try {
        if (id) {
          const updatePayload = {
            id,
            projectId: project.id,
            surface: "top_bar",
            name,
            instructions,
          };
          if (simpleControls !== undefined) updatePayload.simpleControls = simpleControls;
          if (writingSample !== undefined) {
            updatePayload.writingSample = writingSample;
          }
          const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/update", updatePayload);
          const updatedStyle = data?.style;
          if (updatedStyle) {
            setStyleGuideStyles((styles) =>
              styles.map((styleRow) => styleRow.source === "user_style" && styleRow.id === updatedStyle.id ? updatedStyle : styleRow),
            );
            setSelectedStyle((current) =>
              current?.source === "user_style" && current.id === updatedStyle.id
                ? { source: "user_style", id: updatedStyle.id, name: updatedStyle.name }
                : current,
            );
          }

          return updatedStyle;
        }

        const createPayload = {
          projectId: project.id,
          surface: "top_bar",
          source,
          name,
          instructions,
        };
        if (simpleControls !== undefined) createPayload.simpleControls = simpleControls;
        if (writingSample !== undefined) {
          createPayload.writingSample = writingSample;
        }
        const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/create", createPayload);
        const createdStyle = data?.style;
        if (createdStyle) {
          setStyleGuideStyles((styles) => [createdStyle, ...styles]);
          window.dispatchEvent(
            new CustomEvent(STYLE_GUIDE_STYLES_CHANGED_EVENT, { detail: { projectId: project.id } }),
          );
        }
        setSelectedStyle(data?.selectedStyle ?? (createdStyle ? { source: "user_style", id: createdStyle.id, name: createdStyle.name } : null));
        return createdStyle;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [project?.id, subscriptionJSON?.hasAccessToStyleGuide]
  );

  const handleStyleGuideSelect = useCallback(
    async (style) => {
      if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) return;

      try {
        const data = await Auth.fetchWithIdToken("/api/style-settings/styles/select", {
          projectId: project.id,
          surface: "top_bar",
          source: style.source,
          id: style.id,
        });
        const savedSelectedStyle = data?.selectedStyle ?? style;

        setSelectedStyle(savedSelectedStyle);
        setStyleGuideOpen(false);
        setInitialStyleGuideFlow("list");
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [project?.id, subscriptionJSON?.hasAccessToStyleGuide]
  );

  const handleStoryBibleStyleRowSave = useCallback(
    async ({ instructions = "", simpleControls } = {}) => {
      if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) return;

      const normalizedInstructions = typeof instructions === "string" ? instructions : "";

      try {
        const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/update-story-bible", {
          projectId: project.id,
          surface: "top_bar",
          instructions: normalizedInstructions,
          ...(simpleControls !== undefined ? { simpleControls } : {}),
        });
        const savedStyle = data?.style;
        const savedInstructions = savedStyle?.instructions ?? normalizedInstructions;

        useStoryBibleStore.getState().setMetadata?.((draft) => {
          draft.style = savedInstructions;
        });
        setStyleGuideStyles((current) =>
          current.map((row) => (row.source === "story_bible" ? { ...row, ...savedStyle, instructions: savedInstructions } : row)),
        );
        if (lastStylesListResponseRef.current) {
          lastStylesListResponseRef.current = {
            ...lastStylesListResponseRef.current,
            styles: (lastStylesListResponseRef.current.styles ?? []).map((row) =>
              row.source === "story_bible" ? { ...row, ...savedStyle, instructions: savedInstructions } : row,
            ),
          };
        }
        window.dispatchEvent(
          new CustomEvent(STYLE_GUIDE_STYLES_CHANGED_EVENT, { detail: { projectId: project.id } }),
        );
        return savedStyle;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [project?.id, subscriptionJSON?.hasAccessToStyleGuide],
  );

  const handleStoryBibleStyleSave = useCallback(
    async ({ instructions = "" } = {}) => {
      if (!project?.id || !subscriptionJSON?.hasAccessToStyleGuide) return;
      if (selectedStyle?.source === "sudowrite") return;

      const normalizedInstructions = typeof instructions === "string" ? instructions : "";

      if (selectedStyle?.source === "user_style" && selectedStyle.id) {
        const selectedRow = styleGuideStyles.find(
          (row) => row.source === "user_style" && row.id === selectedStyle.id,
        );
        return handleBlankStyleSave({
          id: selectedStyle.id,
          name: selectedRow?.name ?? selectedStyle.name,
          instructions: normalizedInstructions,
        });
      }

      return handleStoryBibleStyleRowSave({ instructions: normalizedInstructions });
    },
    [
      handleBlankStyleSave,
      handleStoryBibleStyleRowSave,
      project?.id,
      selectedStyle,
      styleGuideStyles,
      subscriptionJSON?.hasAccessToStyleGuide,
    ],
  );

  const handleStoryBibleStylePointerClick = useCallback(() => {
    const stylesButton = window.document.querySelector('[data-testid="project-top-bar-style-guide-button"]');
    if (!stylesButton) {
      openStyleSettings("styles");
      return;
    }
    setInitialStyleGuideFlow("list");
    setInitialStyleGuideFlowRequestId((requestId) => requestId + 1);
    setStyleGuideOpen(true);
    stylesButton.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    window.requestAnimationFrame(() => {
      stylesButton.focus({ preventScroll: true });
    });
  }, [openStyleSettings]);

  const handleSetMemory = useStableCallback((val) => {
    project.update({ memory: val });
  });
  const handleSetMemoryEnabled = useStableCallback((val) => {
    project.update({ memoryEnabled: val });
  });
  const handleSetTone = useStableCallback((val) => {
    project.update({ tone: val });
  });
  const handleSetWriteMode = useStableCallback((val) => {
    project.update({ writeMode: val });
  });
  const handleSetDescribeSenses = useStableCallback((val) => {
    project.update({ describeSenses: val });
  });
  const handleSetAutomaticGuidances = useStableCallback((val) => {
    project.update({ automaticGuidances: val });
  });

  const handleStyleGuideClick = useCallback(() => {
    setInitialStyleGuideFlow("list");
    setStyleGuideOpen((open) => !open);
  }, []);

  const handleGeneratedStyleCreated = useCallback(
    (style) => {
      setInitialStyleGuideFlow("list");
      setStyleGuideOpen(false);
      openStyleSettings("styles", style.id);
    },
    [openStyleSettings],
  );

  const handleStyleGuideSettingsClick = useCallback(() => {
    setInitialStyleGuideFlow("list");
    setStyleGuideOpen(false);
    openStyleSettings("styles");
  }, [openStyleSettings]);

  const handleProjectTitleChange = useCallback(() => {
    void loadStyleGuideStyles();
  }, [loadStyleGuideStyles]);

  const handleSettingsSubmit = useStableCallback((data) => {
    setEditorOptions(update(editorOptions, { $merge: data }));
  });

  const shareSlot = useMemo(
    () => (document?.id ? <DocumentSharingMenu documentId={document.id} /> : null),
    [document?.id],
  );

  const helpSlot = useMemo(
    () => (
      <HelpMenu
        setOpen={setHelpOpen}
        open={helpOpen}
        subscription={subscriptionJSON}
        showFront={showFront}
        v2={true}
      />
    ),
    [helpOpen, setHelpOpen, showFront, subscriptionJSON],
  );

  const settingsSlot = useMemo(
    () =>
      userData?.fetched && subscriptionJSON ? (
        <SettingsPanel
          setOpen={setSettingsOpen}
          open={settingsOpen}
          demo={demo}
          setDemo={setDemo}
          subscription={subscriptionJSON}
          font={editorOptions.font}
          fontSize={editorOptions.fontSize}
          textIndent={editorOptions.textIndent}
          lineHeight={editorOptions.lineHeight}
          paragraphSpacing={editorOptions.paragraphSpacing}
          showWordCount={editorOptions.showWordCount}
          creativitySliderPosition={editorOptions.creativitySliderPosition}
          theme={editorOptions.theme}
          showCreditsUsedToast={editorOptions.showCreditsUsedToast}
          onSubmit={handleSettingsSubmit}
          v2={true}
        />
      ) : null,
    [
      demo,
      editorOptions.creativitySliderPosition,
      editorOptions.font,
      editorOptions.fontSize,
      editorOptions.lineHeight,
      editorOptions.paragraphSpacing,
      editorOptions.showCreditsUsedToast,
      editorOptions.showWordCount,
      editorOptions.textIndent,
      editorOptions.theme,
      handleSettingsSubmit,
      setDemo,
      setSettingsOpen,
      settingsOpen,
      subscriptionJSON,
      userData?.fetched,
    ],
  );

  const isOnStepWithHiddenExitButton =
    (activeTourId === TourIds.WRITE_REWRITE && activeTourStepId === "8") ||
    (activeTourId === TourIds.STORY_BIBLE && activeTourStepId === "1");
  const shouldShowTopBarExitTourButton =
    isTourActive &&
    activeTourId !== TourIds.FIND_TOUR_AGAIN &&
    activeTourId !== TourIds.CHAT_ONBOARDING &&
    !isOnStepWithHiddenExitButton;
  const tourActionSlot = useMemo(
    () => (shouldShowTopBarExitTourButton ? <ExitTourButton /> : null),
    [shouldShowTopBarExitTourButton],
  );
  // The paragraph spacing tour points at the settings gear, so the top-bar
  // icons must stay visible alongside the Exit Tour button.
  const tourKeepsTopBarIcons = activeTourId === TourIds.PARAGRAPH_SPACING;

  useEffect(() => {
    setTopBarWordCount(null);
    setTopBarHighlightedWordCount(0);
  }, [document?.id]);

  const emitSudoLayoutEvent = useSudoLayoutEventEmitter();

  const toggleFocusMode = useStableCallback(() => {
    if (columnWidths.right.collapsed && columnWidths.left.collapsed) {
      setRightBarCollapsed(false);
      setLeftBarCollapsed(false);
    } else {
      setRightBarCollapsed(true);
      setLeftBarCollapsed(true);
    }
  });

  const restoreDocument = useStableCallback(() => {
    if (!document?.id) return;
    project.undeleteDocument(document.id).then(() => {
      setDocumentId(contents[0].id);
    });
  });

  const updateSizes = useStableCallback((sizes) => {
    if (userData.fetched) {
      userData.update({
        mainSize: sizes.main,
        rightSize: sizes.right,
      });
    }
  });

  const handleOpenAdmin = useStableCallback(() => {
    setDocumentAdminOpen(true);
  });
  const { can } = useAdminAccess();
  const openAdmin = can("admin.tools") ? handleOpenAdmin : null;

  const onWordCountsChange = useStableCallback(({ wordCount, highlightedWordCount }) => {
    setTopBarWordCount(wordCount);
    setTopBarHighlightedWordCount(highlightedWordCount || 0);
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("sb")) {
      mixpanel.start_session_recording();
      setRightBarCollapsed(true);
      setIsEditingTextarea(true);
    }
  }, []);

  /** Chat comment links can target another manuscript; switch document before EditorContainer expands the card. */
  useEffect(() => {
    const onOpenCommentFromChat = async (e) => {
      const { commentId, documentId: targetDocumentId } = e.detail || {};
      if (!commentId || !targetDocumentId) return;
      const currentId = useProjectStore.getState().document?.id;
      const normalizedTarget = String(targetDocumentId).trim();
      const normalizedCurrent = currentId != null ? String(currentId).trim() : "";
      if (normalizedTarget === normalizedCurrent) return;
      const contentsList = useProjectStore.getState().contents;
      const deletedList = useProjectStore.getState().deletedDocuments;
      if (Array.isArray(contentsList)) {
        const inContents = contentsList.some((item) => item?.id != null && String(item.id).trim() === normalizedTarget);
        const inDeleted =
          Array.isArray(deletedList) && deletedList.some((item) => item?.id != null && String(item.id).trim() === normalizedTarget);
        if (!inContents && !inDeleted) {
          e.stopImmediatePropagation();
          return;
        }
      }
      e.stopImmediatePropagation();
      useProjectStore.getState().setPendingChatCommentNavigation({ commentId, documentId: normalizedTarget });
      try {
        await useProjectStore.getState().setDocumentId(normalizedTarget);
      } catch (err) {
        useProjectStore.getState().setPendingChatCommentNavigation(null);
        Sentry.captureException(err, { tags: { feature: "open_comment_from_chat_nav" } });
      }
    };
    window.addEventListener("sudowrite:open-comment", onOpenCommentFromChat, true);
    return () => window.removeEventListener("sudowrite:open-comment", onOpenCommentFromChat, true);
  }, []);

  // Reset the store on unmount
  useEffect(() => {
    return () => {
      reset();
      toggleComments(false);
    };
  }, []);

  useEffect(() => {
    resetOutline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    // maybe have too many checks for the AbortController here
    // but it ensures that an older async call to set the project
    // doesn't override a newer one, and a user winds up in the wrong project
    const abortController = new AbortController();

    async function run() {
      if (abortController.signal.aborted) return;

      let proj;
      try {
        proj = await ProjectModel.get(id);
      } catch (e) {
        // Firebase permission error - user doesn't have access to this project
        if (e.code === "permission-denied") {
          return setNotFound(true);
        }
        throw e;
      }

      if (!proj) {
        return setNotFound(true);
      }

      // Ensure the current user owns this project (applies to all users, including admins)
      if (proj.data.userId !== user.uid) {
        return setNotFound(true);
      }

      if (abortController.signal.aborted) return;

      ProjectModel.touch(id).catch((e) => {
        console.error("error touching project", e);
      });

      if (!project) {
        setProject(proj);
        setContents(proj.contents);
        setDeletedDocuments(proj.deletedDocuments);
        setWriteMode(proj.writeMode);
        setDescribeSenses(proj.describeSenses);
        setTone(proj.tone);
        setAutomaticGuidances(proj.automaticGuidances);
        setMemory(proj.memory);
        setMemoryEnabled(proj.memoryEnabled);
        setLocked(proj.locked);

        navigateToDoc(proj, hash);
      } else {
        setProject(proj);
      }

      // if (abortController.signal.aborted) return;
      proj.subscribe((updatedProject, updateInfo) => {
        if (abortController.signal.aborted) return;
        setProject(updatedProject);
        setContents(updatedProject.contents);
        setDeletedDocuments(updatedProject.deletedDocuments);
        setWriteMode(updatedProject.writeMode);
        setTone(updatedProject.tone);
        setAutomaticGuidances(updatedProject.automaticGuidances);
        setDescribeSenses(updatedProject.describeSenses);
        setMemory(updatedProject.memory);
        setMemoryEnabled(updatedProject.memoryEnabled);
        setLocked(updatedProject.locked);

        // if this was a document update, refetch the document data
        if (updateInfo?.type === "document_update" && updateInfo?.documentId) {
          // get the current document directly from the store
          // otherwise, document is null due to this useEffect running on mount
          const currDocument = useProjectStore.getState().document;
          if (currDocument?.id === updateInfo.documentId) {
            currDocument.init().then(() => {
              setDocument(currDocument); // re-fetches document data from various sources
            });
          }
        }
      });
    }

    run();

    window.document.body.classList.add("full-height", "project-page");

    return () => {
      abortController.abort();
      if (project) {
        project.release();
      }

      window.document.body.classList.remove("full-height", "project-page");
    };
  }, []);

  useEffect(() => {
    //throw modal happens in rendering
    // set pointer events false on body
    if (locked) {
      window.document.body.style.pointerEvents = "none";
    } else {
      window.document.body.style.pointerEvents = "auto";
    }
  }, [locked]);

  const navigateToDoc = (proj, h) => {
    const docRe = /#(.*)/;
    const matches = h.match(docRe);

    let docId;
    let forceEditorValue = null;
    const contents = proj.contents || [];
    const deletedDocuments = proj.deletedDocuments || [];

    let commentIdFromHash;
    if (matches && matches[1]) {
      // find out if document exists
      const searchParams = new URLSearchParams(matches[1]);
      docId = searchParams.get("doc");
      commentIdFromHash = searchParams.get("comment");
      forceEditorValue = searchParams.get("forceEditor");
    }

    if (forceEditorValue) {
      // Do something with the forceEditorValue here
      console.log(`forceEditorValue is ${forceEditorValue}`);
    }

    if (docId) {
      if (contents.find((item) => item.id === docId)) {
        if (commentIdFromHash && String(commentIdFromHash).trim()) {
          useProjectStore.getState().setPendingChatCommentNavigation({
            commentId: String(commentIdFromHash).trim(),
            documentId: docId,
          });
        }
        setDocumentId(docId, forceEditorValue);
        setIsDeleted(false);
        return;
      }

      if (deletedDocuments.find((item) => item.id === docId)) {
        if (commentIdFromHash && String(commentIdFromHash).trim()) {
          useProjectStore.getState().setPendingChatCommentNavigation({
            commentId: String(commentIdFromHash).trim(),
            documentId: docId,
          });
        }
        setDocumentId(docId, forceEditorValue);
        setIsDeleted(true);
        return;
      }
    }

    if (contents.length) {
      const activeDocumentId = proj.activeDocument && contents.find((item) => item.id === proj.activeDocument)?.id;
      const defaultDocumentId =
        proj.getFirstDocumentIdInSidebarOrder() ||
        [...contents]
          .map((item) => ({ ...item, displayOrder: parseFloat(item.displayOrder || 0) }))
          .sort((a, b) => a.displayOrder - b.displayOrder)[0]?.id;
      const resolvedId = activeDocumentId || defaultDocumentId;
      if (resolvedId) {
        setDocumentId(resolvedId, forceEditorValue);
        setIsDeleted(false);
        return;
      }
    }

    // No documents to navigate to. Clear any stale document state.
    useProjectStore.setState({ selectedDocumentId: null });
    setDocument(null);
    setIsDeleted(false);
  };

  /**
   * This is the main call to change to a different document to show, base on the id
   *
   * @param {String} id ID of the document
   */
  // const setDocumentId = async (id, forceEditorValue = null, scrollTo = null) => {
  //   const newDocument = new DocumentModel({ id: id, userId: user.uid });
  //   await newDocument.init();
  //   setDocument({ id });
  //   if (id === document?.id && editorSectionRef?.current?.scrollIntoView) {
  //     editorSectionRef.current.scrollIntoView({ behavior: scrollTo ? "smooth" : "instant" });
  //   }
  // };

  useDocumentUrlSync({ document, project, hash, history });

  useEffect(() => {
    if (document?.id) {
      resetDocumentDependentState();
    }
  }, [document?.id, resetDocumentDependentState]);

  useEffect(() => {
    if (project) {
      navigateToDoc(project, hash);
    }
  }, [hash]);

  useEffect(() => {
    if (!project?.id || !importSpeedPerceptionActive) return;
    const wip = project.importStep && project.importStep !== "CANCELLED";
    if (wip) {
      if (!importRestrictedUiRef.current.active) {
        const sb = useStoryBibleStore.getState().isActive;
        const { left, right } = useProjectStore.getState().columnWidths;
        importRestrictedUiRef.current = {
          active: true,
          savedSb: sb,
          savedLeftCollapsed: left.collapsed,
          savedRightCollapsed: right.collapsed,
        };
        setLeftBarCollapsed(true);
        setRightBarCollapsed(true);
        useStoryBibleStore.getState().setIsActive(false);
      }
    } else if (importRestrictedUiRef.current.active) {
      const { savedSb, savedLeftCollapsed, savedRightCollapsed } = importRestrictedUiRef.current;
      importRestrictedUiRef.current = {
        active: false,
        savedSb: null,
        savedLeftCollapsed: null,
        savedRightCollapsed: null,
      };
      if (savedLeftCollapsed === false) {
        setLeftBarCollapsed(false);
      }
      if (savedRightCollapsed === false) {
        setRightBarCollapsed(false);
      }
      if (savedSb === true) {
        useStoryBibleStore.getState().setIsActive(true);
      } else if (savedSb === null) {
        useStoryBibleStore.getState().setIsActive(null);
      }
    }
  }, [project?.id, project?.importStep, importSpeedPerceptionActive, setLeftBarCollapsed, setRightBarCollapsed]);

  useEffect(() => {
    const hasSeen = !!userData.data.hasSeenSBModal;
    if (hasSeen) return;
    const isNewUser = userData.data.documentsOrder?.length < 2;
    if (isSW2 && !hasSeen && !isNewUser) {
      setShowSBmodal(true);
    }
    // Skip showing for new users
    if (isNewUser) {
      userData.update({ hasSeenSBModal: true });
    }
  }, [isSW2, userData]);

  if (notFound) {
    return (
      <PageNotFound
        disableHeader={true}
        message="This page does not exist."
      />
    );
  }

  if (!project) {
    return null;
  }

  const isCenterToolbarCollapsed = Boolean(document && columnWidths.center.collapsed);

  return (
    <StyledContainer
      className={classNames({
        "project-component-container": true,
        sw2: isSW2,
        "story-bible-in-view": storyBibleInView,
        "style-guide-menu-open": isSW2 && !!subscriptionJSON?.hasAccessToStyleGuide && styleGuideOpen,
      })}
    >
      {document && (
        <DocumentAdminMenu
          document={document}
          open={documentAdminOpen}
          setOpen={setDocumentAdminOpen}
          close={() => setDocumentAdminOpen(false)}
        />
      )}

      <ProjectScenesUpdate />

      <div
        className={classNames({ inner: true, sw2: isSW2 })}
        ref={innerRef}
        id="project"
      >
        {isSW2 && project && (
          <ProjectTopBar
            project={project}
            saved={saved}
            wordCount={topBarWordCount}
            highlightedWordCount={topBarHighlightedWordCount}
            showWordCount={!!editorOptions.showWordCount}
            collapsed={isCenterToolbarCollapsed}
            showStyleGuideControl={!!subscriptionJSON?.hasAccessToStyleGuide}
            selectedStyleName={selectedStyleName}
            selectedStyle={selectedStyle}
            styleGuideStyles={styleGuideStyles}
            styleGuideOpen={styleGuideOpen}
            initialStyleGuideFlow={initialStyleGuideFlow}
            initialStyleGuideFlowRequestId={initialStyleGuideFlowRequestId}
            onStyleGuideClick={handleStyleGuideClick}
            onStyleGuideSelect={handleStyleGuideSelect}
            onBlankStyleSave={handleBlankStyleSave}
            onStoryBibleStyleSave={handleStoryBibleStyleRowSave}
            onGeneratedStyleCreated={handleGeneratedStyleCreated}
            onProjectTitleChange={handleProjectTitleChange}
            onStyleGuideSettingsClick={handleStyleGuideSettingsClick}
            shareSlot={shareSlot}
            helpSlot={helpSlot}
            settingsSlot={settingsSlot}
            tourActionSlot={tourActionSlot}
            tourActionKeepsIcons={tourKeepsTopBarIcons}
          />
        )}
        <div
          className={classNames({ "project-menu": true, sw2: isSW2, collapsed: isCenterToolbarCollapsed })}
          ref={menuRef}
        >
          {isDocumentRouteLoading && (
            <div
              className="sw2-menu-loading-placeholder"
              aria-hidden="true"
            >
              {[
                { label: "Write", icon: "/assets/write.svg" },
                { label: "Rewrite", icon: "/assets/rewrite-icon.svg" },
                { label: "Describe", icon: "/assets/describe-icon.svg" },
                { label: "Feedback", icon: "/assets/feedback-developmental.svg" },
                { label: "Brainstorm", icon: "/assets/brainstorm-icon.svg" },
                { label: "Visualize", icon: "/assets/Visualize.svg" },
                { label: "More Tools", icon: "/assets/plugin.svg" },
              ].map(({ label, icon }) => (
                <span
                  className="sw2-menu-loading-tool"
                  key={label}
                >
                  <img
                    src={icon}
                    alt=""
                  />
                  <span>{label}</span>
                </span>
              ))}
            </div>
          )}
          {!isDocumentRouteLoading && !document && !selectedDocumentId && project?.fetched && (
            <Menu
              project={project}
              menuRef={menuRef}
              describeSenses={describeSenses}
              setDescribeSenses={(val) => {
                project.update({ describeSenses: val });
              }}
              writeMode={writeMode}
              writeOpen={false}
              setWriteOpen={() => {}}
              setDescribeOpen={() => {}}
              setWriteMode={(val) => {
                project.update({ writeMode: val });
              }}
              guidanceOpen={false}
              loadingGuidances={false}
              closeGuidance={() => {}}
              openGuidance={() => {}}
              setToneOpen={() => {}}
              modalOpen={false}
              setModalOpen={() => {}}
              setTone={(val) => {
                project.update({ tone: val });
              }}
              tones={TONES}
              tone={tone}
              labsOpen={false}
              openDescription={() => {}}
              openFilter={() => {}}
              openBrainstorm={() => {}}
              openFeedback={() => {}}
              setStoryToolOpen={() => {}}
              openWormhole={() => {}}
              setLabsOpen={() => {}}
              hasAccessToFeatures={subscription?.hasAccessToFeatures}
              documentId={null}
              fetchGuidance={() => {}}
              guidances={[]}
              automaticGuidances={automaticGuidances}
              setAutomaticGuidances={handleSetAutomaticGuidances}
              setGuidances={() => {}}
              memory={memory}
              setMemory={handleSetMemory}
              memoryEnabled={memoryEnabled}
              setMemoryEnabled={handleSetMemoryEnabled}
              wordCount={0}
              highlightedWordCount={0}
              editorOptions={editorOptions}
              saved={false}
              openVisualize={() => {}}
              wormholeOutputLength={wormholeOutputLength}
              wormholeOutputCards={wormholeOutputCards}
              creativitySliderPosition={creativitySliderPosition}
              setEditorOptions={setEditorOptions}
              openAdmin={openAdmin}
              runDocumentPluginAction={() => {}}
              documentPluginBridge={{}}
              showFront={showFront}
              setCommandModeState={() => {}}
              showFirstDraft={false}
              writeModel={writeModel}
              trashedItem={null}
              hasDocument={false}
              minimal={true}
              hideHelp={false}
            />
          )}
        </div>

        <Banners>
          <EmailReviewBanner userId={user?.uid} isPaid={subscription.activeSubscription || subscription.hasPaid} />
          <MultipleAccountCTA />

          <QuotaZeroCTA
            subscription={subscription}
            userData={userData}
          />

          <QuotaMessageV1 subscription={subscription} />

          <OverageNotice subscription={subscription} />

          {subscription.activeSubscription && !subscription.goodStanding && (
            <EditorMessage
              message="We failed to charge the payment method for your subscription. Please update it in order to continue accessing Sudowrite features."
              buttonText="Update Payment"
              url="/app/subscription/portal"
              history={history}
            />
          )}

          {subscription.data?.taxLocationIssue && !subscription.data?.paused && (
            <EditorMessage
              message="We couldn't process your latest subscription invoice because your billing address is incomplete. Please update it to avoid interruption or restore AI access."
              buttonText="Update Billing Address"
              url="/app/subscription/portal"
              history={history}
            />
          )}

          <GraceCTA
            subscription={subscription}
            uid={getUserId()}
            userData={userData}
          />
        </Banners>

        <div className={classNames({ "project-main": true, sw2: isSW2, "center-collapsed": isCenterToolbarCollapsed })}>
          <LeftBarContainer
            setCollapsed={setLeftBarCollapsed}
            propWidth={columnWidths.left.current}
          />

          <div className={classNames({ editor: true, sw2: isSW2 })}>
            {document && (
              <TiptapDocumentSB
                emitSudoLayoutEvent={emitSudoLayoutEvent}
                editorSectionRef={editorSectionRef}
                user={user}
                deleted={isDeleted}
                restoreDocument={restoreDocument}
                height={leftBarHeight}
                document={document}
                documentId={document.id}
                menuRef={menuRef}
                setSettingsOpen={setSettingsOpen}
                setHelpOpen={setHelpOpen}
                theme={theme}
                tone={tone}
                project={project}
                contents={contents}
                memory={memory}
                setMemory={handleSetMemory}
                memoryEnabled={memoryEnabled}
                setMemoryEnabled={handleSetMemoryEnabled}
                setTone={handleSetTone}
                writeMode={writeMode}
                setWriteMode={handleSetWriteMode}
                describeSenses={describeSenses}
                setDescribeSenses={handleSetDescribeSenses}
                automaticGuidances={automaticGuidances}
                setAutomaticGuidances={handleSetAutomaticGuidances}
                totalDocuments={totalDocuments}
                subscription={subscription}
                customizableWormhole={subscription.data.customizableWormhole}
                needsFeedback={needsFeedback}
                updateNeedsFeedback={updateNeedsFeedback}
                setRightBarCollapsed={setRightBarCollapsed}
                columnWidths={columnWidths}
                hideEDUCards={hideEDUCards}
                updateHideEDUCards={updateHideEDUCards}
                editorOptions={editorOptions}
                setEditorOptions={setEditorOptions}
                wormholeOutputLength={wormholeOutputLength}
                wormholeOutputCards={wormholeOutputCards}
                creativitySliderPosition={creativitySliderPosition}
                summaryEnabled={summaryEnabled}
                expandEnabled={expandEnabled}
                filterEnabled={filterEnabled}
                updateSizes={updateSizes}
                userData={userData}
                editingTitle={editingTitle}
                leftBarCollapsed={columnWidths.left.collapsed}
                openAdmin={openAdmin}
                saved={saved}
                setSaved={setSaved}
                onWordCountsChange={onWordCountsChange}
                isOnline={isOnline}
                showFront={showFront}
                isEditing={isEditing}
                setDocumentId={setDocumentId}
                writeModel={writeModel}
                isEditingDocumentTitle={isEditingDocumentTitle}
                trashedItem={trashedItem}
                toggleFocusMode={toggleFocusMode}
                onStoryBibleStylePointerClick={handleStoryBibleStylePointerClick}
                onStoryBibleStyleSave={handleStoryBibleStyleSave}
                styleGuideDisplayStyle={storyBibleStyleText}
                selectedStyleSource={selectedStyle?.source}
                selectedStyleName={selectedStyleName}
              />
            )}
            {!document && contents?.length === 0 && !project?.importStep && (
              <div className="empty-project">
                <div className="empty-project-content">
                  <h2 className="text-xl font-semibold">No Documents Yet</h2>
                  <p>Create a document to start writing.</p>
                  <SudoButton
                    variant="primary"
                    disabled={creatingDocument}
                    onClick={async () => {
                      if (creatingDocument) return;
                      setCreatingDocument(true);
                      setIsCreatingNewBlankDocument(true);
                      try {
                        const result = await project.createDocument();
                        if (result?.document?.id) {
                          setDocumentId(result.document.id);
                        }
                      } catch (error) {
                        console.error("error creating document", error);
                      } finally {
                        setCreatingDocument(false);
                      }
                    }}
                  >
                    Create a Document
                  </SudoButton>
                </div>
              </div>
            )}
          </div>
        </div>

        {importSpeedPerceptionActive && (
          <ImportProgressBanner
            projectId={project?.id}
            initialImportStep={project?.importStep}
            initialImportSource={project?.importSource}
            initialHasMyVoiceTraining={project?.hasMyVoiceTraining}
          />
        )}
        <ProjectSpacingMigrationProgressBanner
          projectId={project?.id}
          onNavigateDocument={setDocumentId}
        />
      </div>
    </StyledContainer>
  );
}
